import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Signup.css';

function Signup({ setUserId, setMessage, setIsAuthenticated }) {
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [errors, setErrors] = useState({
    loginName: '', password: '', confirmPassword: '', email: '', nickname: '',
  });
  const [isValid, setIsValid] = useState({
    loginName: false, password: false, confirmPassword: false, email: true, nickname: false,
  });
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false, uppercase: false, number: false, specialChar: false, noSpaces: true,
  });
  const navigate = useNavigate();

  const apiUrl = '/api';  // Updated to relative path

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await axios.get(`${apiUrl}/csrf-token`, { withCredentials: true });
        console.log('CSRF token fetched successfully:', response.data.csrfToken);
        setCsrfToken(response.data.csrfToken);
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error.message, error.response?.data);
        setMessage('Failed to fetch CSRF token. Please refresh the page.');
      }
    };
    fetchCsrfToken();
  }, [setMessage]);

  const validateField = (fieldName, value, compareValue = null) => {
    let error = '';
    let valid = false;

    switch (fieldName) {
      case 'loginName':
        const trimmedLoginName = value.trim();
        if (trimmedLoginName.length < 5 || trimmedLoginName.length > 15) {
          error = 'Login name must be 5-15 characters';
        } else if (!/^[a-zA-Z0-9]+$/.test(trimmedLoginName)) {
          error = 'Login name must be alphanumeric';
        } else {
          valid = true;
        }
        break;

      case 'password':
        const trimmedPassword = value.trim();
        const criteria = {
          minLength: trimmedPassword.length >= 6,
          uppercase: /[A-Z]/.test(trimmedPassword),
          number: /[0-9]/.test(trimmedPassword),
          specialChar: /[!@#$%^&*]/.test(trimmedPassword),
          noSpaces: !/\s/.test(trimmedPassword),
        };
        setPasswordCriteria(criteria);
        if (!trimmedPassword) {
          error = 'Password is required';
        } else if (!Object.values(criteria).every(Boolean)) {
          error = '';
        } else {
          valid = true;
        }
        if (confirmPassword) validateField('confirmPassword', confirmPassword, trimmedPassword);
        break;

      case 'confirmPassword':
        const trimmedConfirmPassword = value.trim();
        if (!trimmedConfirmPassword) {
          error = 'Confirm password is required';
        } else if (trimmedConfirmPassword !== compareValue) {
          error = 'Passwords do not match';
        } else {
          valid = isValid.password;
        }
        break;

      case 'email':
        const trimmedEmail = value.trim();
        if (trimmedEmail && !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(trimmedEmail)) {
          error = 'Invalid email format';
        } else {
          valid = true;
        }
        break;

      case 'nickname':
        const trimmedNickname = value.trim();
        if (trimmedNickname.length < 1 || trimmedNickname.length > 20) {
          error = 'Nickname must be 1-20 characters';
        } else if (!/^[a-zA-Z0-9]+$/.test(trimmedNickname)) {
          error = 'Nickname must be alphanumeric';
        } else {
          valid = true;
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    setIsValid((prev) => ({ ...prev, [fieldName]: valid }));
  };

  const debouncedValidate = debounce(validateField, 300);

  const handleInputChange = (fieldName, value, compareValue = null) => {
    switch (fieldName) {
      case 'loginName': setLoginName(value); debouncedValidate('loginName', value); break;
      case 'password': setPassword(value); debouncedValidate('password', value); break;
      case 'confirmPassword': setConfirmPassword(value); debouncedValidate('confirmPassword', value, password); break;
      case 'email': setEmail(value); debouncedValidate('email', value); break;
      case 'nickname': setNickname(value); debouncedValidate('nickname', value); break;
      default: break;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage('');
    setErrors({ loginName: '', password: '', confirmPassword: '', email: '', nickname: '' });

    const trimmedLoginName = loginName.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const trimmedEmail = email.trim();
    const trimmedNickname = nickname.trim();

    const newErrors = { loginName: '', password: '', confirmPassword: '', email: '', nickname: '' };
    if (trimmedLoginName.length < 5 || trimmedLoginName.length > 15) newErrors.loginName = 'Login name must be 5-15 characters';
    if (!/^[a-zA-Z0-9]+$/.test(trimmedLoginName)) newErrors.loginName = newErrors.loginName ? `${newErrors.loginName}, must be alphanumeric` : 'Login name must be alphanumeric';
    if (!trimmedPassword) newErrors.password = 'Password is required';
    if (!trimmedConfirmPassword) newErrors.confirmPassword = 'Confirm password is required';
    if (trimmedPassword !== trimmedConfirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (trimmedEmail && !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(trimmedEmail)) newErrors.email = 'Invalid email format';
    if (trimmedNickname.length < 1 || trimmedNickname.length > 20) newErrors.nickname = 'Nickname must be 1-20 characters';
    if (!/^[a-zA-Z0-9]+$/.test(trimmedNickname)) newErrors.nickname = newErrors.nickname ? `${newErrors.nickname}, must be alphanumeric` : 'Nickname must be alphanumeric';

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    if (!csrfToken) {
      setMessage('CSRF token not available. Please refresh the page.');
      return;
    }

    const payload = { loginName: trimmedLoginName, password: trimmedPassword, email: trimmedEmail, nickname: trimmedNickname };
    try {
      const response = await axios.post(`${apiUrl}/signup`, payload, {
        headers: { 'X-CSRF-Token': csrfToken },
        withCredentials: true,
      });
      console.log('Signup response:', response.data, 'Set-Cookie:', response.headers['set-cookie']);
      if (response.data.success) {
        setUserId(response.data.userId);
        setIsAuthenticated(true);
        setLoginName('');
        setPassword('');
        setConfirmPassword('');
        setEmail('');
        setNickname('');
        setMessage('Signup successful!');

        // Verify cookie immediately
        const authResponse = await axios.get(`${apiUrl}/me`, { withCredentials: true });
        console.log('Post-signup /api/me:', authResponse.data);

        navigate('/chat');
      } else {
        setMessage(response.data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup request failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      setMessage(error.response?.data?.message || 'Signup failed');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <form onSubmit={handleSignup}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="loginName">Login Name:</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="loginName"
                  placeholder="5-15 chars, alphanumeric"
                  value={loginName}
                  onChange={(e) => handleInputChange('loginName', e.target.value)}
                />
                {loginName && (isValid.loginName ? <span className="checkmark">✔</span> : <span className="invalid">✖</span>)}
              </div>
            </div>
            {errors.loginName && <p className="error">{errors.loginName}</p>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password:</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="password"
                  placeholder="Min 6 chars, 1 upper, 1 num, 1 special"
                  value={password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                />
                {password && (isValid.password ? <span className="checkmark">✔</span> : <span className="invalid">✖</span>)}
              </div>
            </div>
            {password && (
              <ul className="criteria-list">
                <li className={passwordCriteria.minLength ? 'criteria-met' : 'criteria-not-met'}>
                  {passwordCriteria.minLength ? '✅' : '❌'} At least 6 characters
                </li>
                <li className={passwordCriteria.uppercase ? 'criteria-met' : 'criteria-not-met'}>
                  {passwordCriteria.uppercase ? '✅' : '❌'} At least one uppercase letter
                </li>
                <li className={passwordCriteria.number ? 'criteria-met' : 'criteria-not-met'}>
                  {passwordCriteria.number ? '✅' : '❌'} At least one number
                </li>
                <li className={passwordCriteria.specialChar ? 'criteria-met' : 'criteria-not-met'}>
                  {passwordCriteria.specialChar ? '✅' : '❌'} At least one special character (!@#$%^&*)
                </li>
                <li className={passwordCriteria.noSpaces ? 'criteria-met' : 'criteria-not-met'}>
                  {passwordCriteria.noSpaces ? '✅' : '❌'} No spaces
                </li>
              </ul>
            )}
            {errors.password && <p className="error">{errors.password}</p>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password:</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="confirmPassword"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                />
                {confirmPassword && (isValid.confirmPassword ? <span className="checkmark">✔</span> : <span className="invalid">✖</span>)}
              </div>
            </div>
            {errors.confirmPassword && <p className="error">{errors.confirmPassword}</p>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  placeholder="Optional"
                  value={email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
                {email && (isValid.email ? <span className="checkmark">✔</span> : <span className="invalid">✖</span>)}
              </div>
            </div>
            {errors.email && <p className="error">{errors.email}</p>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nickname">Nickname:</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="nickname"
                  placeholder="1-20 chars, alphanumeric"
                  value={nickname}
                  onChange={(e) => handleInputChange('nickname', e.target.value)}
                />
                {nickname && (isValid.nickname ? <span className="checkmark">✔</span> : <span className="invalid">✖</span>)}
              </div>
            </div>
            {errors.nickname && <p className="error">{errors.nickname}</p>}
          </div>

          <button type="submit" disabled={!(isValid.loginName && isValid.password && isValid.confirmPassword && isValid.nickname)}>
            Sign Up
          </button>
        </form>
      </div>
    </div>
  );
}

export default Signup;